import React, {ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useRuleEditorContext} from '../../../../editors/ruleEditor/ruleEditorContext';
import {ReplyAction, RuleActionTypesEnum} from '../../../ruleActionModel';
import {RuleReplyActionCard} from '../../cards/ruleReplyActionCard';
import {RuleActionCardProps} from '../../ruleActionCard';
import {RuleActionsList} from '../../ruleActionsList';
import {useRuleActionGroups} from '../../ruleEditorActions';
import {RuleEditorSection} from '../../ruleEditorSection';
import {useRuleActionWithParams} from '../helpers/recipeHooksHelpers';
import {buildRuleActionCardsProps} from '../helpers/recipesHelpers';
import {CsatEditorStateActions} from './hooks/useCsatEditorState';
import {CsatEditorState} from './state/csatEditorState';

/*
 * Props.
 */

interface CsatEditorSentActionsProps {
  state: CsatEditorState;
  description: ReactNode;

  actions: CsatEditorStateActions;
}

/*
 * Styles.
 */

const RuleActionWrapper = styled.div`
  padding-bottom: 15px;
`;

/*
 * Component.
 */

export const CsatEditorSentAction: React.FC<CsatEditorSentActionsProps> = (props) => {
  const {state, description} = props;

  const {ruleIntegration} = useRuleEditorContext();
  const actionGroups = useRuleActionGroups(ruleIntegration);

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle();

  const replyActionParam = useMemo(
    () => ({
      response: state.csatState.response ?? 0,
      when: 'always',
    }),
    [state.csatState.response],
  );

  const replyAction = useRuleActionWithParams(RuleActionTypesEnum.REPLY, replyActionParam);

  const replyActionProps: RuleActionCardProps = {
    action: replyAction,
    actionDefinition: ruleIntegration.findActionDefinition(replyAction),
    onUpdateAction: (action) => {
      if (action.type !== RuleActionTypesEnum.REPLY) {
        return;
      }

      props.actions.onSetResponseId(action.params.response ?? 0);
    },
    onValidation: props.actions.onValidation,
  };

  const tagActionParam = useMemo(
    () => ({
      tag: state.csatState.tags.sent,
    }),
    [state.csatState.tags.sent],
  );

  const tagAction = useRuleActionWithParams(RuleActionTypesEnum.TAG, tagActionParam);

  const tagActionProps: RuleActionCardProps = {
    action: tagAction,
    actionDefinition: ruleIntegration.findActionDefinition(tagAction),
    onUpdateAction: (action) => {
      if (action.type === RuleActionTypesEnum.TAG && action.params.tag) {
        props.actions.onSetSentTag(action.params.tag);
      }
    },
    onValidation: props.actions.onValidation,
  };

  const untagActionParam = useMemo(
    () => ({
      tag: state.csatState.tags.scheduled,
    }),
    [state.csatState.tags.scheduled],
  );

  const untagAction = useRuleActionWithParams(RuleActionTypesEnum.UNTAG, untagActionParam);

  const untagActionProps: RuleActionCardProps = {
    action: untagAction,
    actionDefinition: ruleIntegration.findActionDefinition(untagAction),
    onUpdateAction: (action) => {
      if (action.type === RuleActionTypesEnum.UNTAG && action.params.tag) {
        props.actions.onSetScheduledTag(action.params.tag);
      }
    },
    onValidation: props.actions.onValidation,
  };

  const actions = buildRuleActionCardsProps(
    state.csatState.actions.sent,
    ruleIntegration,
    props.actions.onUpdateSentAction,
    props.actions.onValidation,
    props.actions.onDeleteSentAction,
  );

  return (
    <RuleEditorSection
      description={description}
      shouldShowMoreOptions={shouldShowMoreOptions}
      toggleMoreOptions={toggleMoreOptions}
    >
      <RuleActionWrapper>
        <RuleReplyActionCard
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          action={replyActionProps.action as ReplyAction}
          actionDefinition={replyActionProps.actionDefinition}
          onUpdate={replyActionProps.onUpdateAction}
          isCsat
        />
      </RuleActionWrapper>
      <RuleActionsList
        actions={[tagActionProps, untagActionProps, ...actions]}
        actionGroups={actionGroups}
        onAddAction={props.actions.onAddSentAction}
        shouldShowMoreOptions={shouldShowMoreOptions}
      />
    </RuleEditorSection>
  );
};
